import { authExchange as urqlAuthExchange } from '@urql/exchange-auth';
import { shouldAttemptAuthRecovery } from '@/client/exchanges/authFailure';
import { graphql } from '@/gql/graphql';
import getAuth, { getIdTokenResult } from '@/utils/auth';
import getToken from '@/utils/getToken';

const missingFirebaseUserMessage =
  'Could not refresh Hasura auth token without a Firebase user.';
const missingFirebaseTokenMessage =
  'Could not refresh Hasura auth token without a Firebase ID token.';
const missingClaimsMessage = 'Could not generate Hasura claims.';
const missingHasuraTokenMessage = 'Could not refresh Hasura auth token.';

const GenerateClaims = graphql(/* GraphQL */ `
  mutation GenerateClaims($token: String!) {
    generateClaims(token: $token) {
      __typename
      id
    }
  }
`);

export default urqlAuthExchange(async (utils) => {
  const auth = getAuth();

  let token = await getToken();

  return {
    addAuthToOperation(operation) {
      if (!token) {
        return operation;
      }

      return utils.appendHeaders(operation, {
        Authorization: `Bearer ${token}`,
      });
    },
    willAuthError() {
      return !token;
    },
    didAuthError(error) {
      return shouldAttemptAuthRecovery(error);
    },
    async refreshAuth() {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        token = null;
        throw new Error(missingFirebaseUserMessage);
      }

      const idTokenResult = await getIdTokenResult(currentUser);
      const claims = idTokenResult?.claims['https://hasura.io/jwt/claims'];

      if (!idTokenResult?.token) {
        token = null;
        throw new Error(missingFirebaseTokenMessage);
      }

      if (!claims) {
        const result = await utils.mutate(GenerateClaims, {
          token: idTokenResult.token,
        });

        if (result.error) {
          token = null;
          throw result.error;
        }

        if (!result.data?.generateClaims?.id) {
          token = null;
          throw new Error(missingClaimsMessage);
        }
      }

      token = await getToken(true);

      if (!token) {
        throw new Error(missingHasuraTokenMessage);
      }
    },
  };
});
